import { Trans } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";
import styles from "./index.module.scss";
import type { GroupingState, SortingState, Row } from "@tanstack/react-table";
import {
  useReactTable,
  createColumnHelper,
  flexRender,
  getSortedRowModel,
  getCoreRowModel,
  getFilteredRowModel,
} from "@tanstack/react-table";
import { useSelectedDate } from "@/Utils/useSelectedDate";
import {
  activityCategoryDictionary,
  displayCancelGroupReason,
  isCancelledWithReason,
  useActivityOccurrencesAndGroups,
} from "@/api/Activities";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { deducedError } from "@/Utils/ErrorUtils";
import { memo, useMemo, useState } from "react";
import {
  type IActivityCategory,
  type IActivityOccurrenceOrGroup,
  type ITimeOfDay,
  orderedCategories,
} from "@medoma/models/activities";
import {
  activityOccurrenceStatusSchema,
  categorySchema,
  getActivityCategoryConfig,
  isEmployeePerformed,
  isGroup,
  timeOfDaySchema,
  groupStatusSchema,
} from "@medoma/models/activities";
import { ActorsCell } from "./ActorsCell";
import { getPatientNameWithStatus, useTeams } from "@/api/Patients";
import TeamBadge from "@/components/Teams/TeamBadge";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import {
  activityRowStatusOrder,
  calculateGroupStatus,
  resolveAreaDisplayName,
  resolveTeam,
  type IActivityRowStatus,
} from "./getDataFromActivityOccurrences";
import {
  getPatientFullName,
  patientStatusDictionary,
  patientStatusSchema,
  type IDeletedPatient,
  type IExistingPatient,
  type IPatient,
  type ITeam,
} from "@medoma/models/patients";
import * as Sentry from "@sentry/react";
import type { IEmployee, IScheduledShift } from "@medoma/models/shifts";
import { shiftName, shiftTimeDisplayValues } from "@medoma/models/shifts";
import { customGetGroupedRowModel } from "./customGetGroupedRowModel";
import {
  matchesPatientOrActivityTitleSearch,
  showStatusForActivityOccurrence,
} from "./activitiesTableUtils";
import { getTitleFromActivityOccurrenceOrGroup } from "../../Planning/SideBar/planningSideBarSortingUtils";
import { TimeSpan } from "@/components/Time/TimeSpan";
import { z } from "zod";
import { statusDictionary } from "@/components/StatusTag/statusTagUtils";
import NoResults from "@/components/NoResults/NoResults";
import { InlineAlert } from "@medoma/components/InlineAlert/InlineAlert";
import { useLingui } from "@lingui/react/macro";
import { SplitPane } from "@/components/SplitPane/SplitPane";
import { Outlet, useNavigate, useParams } from "react-router";
import { Heading } from "@medoma/components/Heading/Heading";
import { Text } from "@medoma/components/Text/Text";
import { ActivityTitle } from "@/components/ActivityTitle/ActivityTitle";
import { StatusTag } from "@/components/StatusTag/StatusTag";
import clsx from "clsx";
import { ControlledSelect } from "@medoma/components/Select/Select";
import { dateName } from "@/Utils/DateUtils";
import StandaloneCheckbox from "@/components/Checkbox/StandaloneCheckbox";
import Fieldset from "@medoma/components/Fieldset/Fieldset";
import { useCurrentEmployee } from "@/api/Employee";
import ControlledInput from "@medoma/components/InputField/ControlledInput";
import DurationChip from "@/components/Chips/DurationChip";
import { calculateGroupDuration } from "@/components/Chips/calculateGroupDuration";
import { knownFeatureFlagsSchema, useFeatureFlag } from "@/api/FeatureFlags";
import { FlagsPopover } from "@/components/FlagsPopover/FlagsPopover";
import { useActivitiesViewToolbar } from "../useActivitiesViewToolbar";
import { KeyToPatientPopover } from "@/components/KeyToPatientPopover/KeyToPatientPopover";
import { PatientResponsiblePopover } from "@/components/PatientResponsiblePopover/PatientResponsiblePopover";
import { PetPopover } from "@/components/PetPopover/PetPopover";

type IActivityTableItem = {
  activityOccurrenceOrGroup: IActivityOccurrenceOrGroup;
  category: IActivityCategory;
  timespan: [Date, Date, ITimeOfDay];
  status: IActivityRowStatus | undefined;
  title: IActivityOccurrenceOrGroup;
  patient?:
    | IDeletedPatient
    | Pick<
        IExistingPatient,
        | "id"
        | "name"
        | "status"
        | "team"
        | "flags"
        | "address"
        | "pet"
        | "primaryResponsible"
      >
    | null;
  areaDisplayName: string | undefined;
  actors: IActivityOccurrenceOrGroup;
  team: ITeam | undefined;
  currentUser?: IEmployee;
  occurrenceIsActive: boolean;
  activityIsActive: boolean;
  groupIsActive: boolean;
  activeOccurrencesInGroup: { activityId: string; occurrenceId: string }[];
  activeActivitiesInGroup: { activityId: string }[];
  estimatedDuration: number;
};

const groupingSchema = z.discriminatedUnion("id", [
  z.object({ id: z.literal("actors"), value: z.string() }),
  z.object({ id: z.literal("category"), value: categorySchema }),
  z.object({ id: z.literal("patient"), value: z.string() }),
  z.object({
    id: z.literal("status"),
    value: z.union([activityOccurrenceStatusSchema, groupStatusSchema]),
  }),
  z.object({ id: z.literal("team"), value: z.string() }),
]);
type IGrouping = z.infer<typeof groupingSchema>;

const GROUPABLE_COLUMNS = [
  "category",
  "status",
  "patient",
  "actors",
  "team",
] as const satisfies readonly IGrouping["id"][];
const NO_GROUPING_VALUE = "none";
const SORTABLE_COLUMNS = ["timespan", "status", "patient"] as const;
const COLUMN_DISPLAY_NAMES = {
  category: msg`Aktivitetstyp`,
  patient: msg`Patient`,
  status: msg`Status`,
  timespan: msg`Tid`,
  actors: msg`Utförare`,
  team: msg`Team`,
  areaDisplayName: msg`Område`,
};

const NOT_ASSIGNABLE = "NOT_ASSIGNABLE";
const NO_ONE_ASSIGNED = "NO_ONE_ASSIGNED";
const MULTIPLE_ASSIGNED = "MULTIPLE_ASSIGNED";

const columnHelper = createColumnHelper<IActivityTableItem>();

const columns = (
  translator: ReturnType<typeof useLingui>["t"],
  shouldShowTeam: boolean,
  showDuration: boolean,
  expandedRowLayout: boolean,
) => [
  columnHelper.accessor("category", {
    header: translator(COLUMN_DISPLAY_NAMES["category"]),
    cell: ({ getValue }) => {
      return <CategoryIcon category={getValue()} size="small" />;
    },
    sortingFn: (a, b) => {
      const aState = a.getValue<IActivityCategory>("category");
      const bState = b.getValue<IActivityCategory>("category");

      // Sort by orderedCategories
      if (
        orderedCategories.indexOf(aState) < orderedCategories.indexOf(bState)
      ) {
        return -1;
      } else if (
        orderedCategories.indexOf(aState) > orderedCategories.indexOf(bState)
      ) {
        return 1;
      } else {
        return 0;
      }
    },
  }),
  columnHelper.accessor("timespan", {
    header: translator(COLUMN_DISPLAY_NAMES["timespan"]),
    cell: ({ getValue, row: { original } }) => {
      const [start, end, type] = getValue();
      const categoryAllowsDuration = getActivityCategoryConfig({
        category: original.category,
        activityHasPatient: Boolean(original.patient),
      }).allowsDuration;
      return (
        <div className={styles.timespanCell}>
          <TimeSpan
            timespan={type === timeOfDaySchema.enum.Any ? type : { start, end }}
          />
          {showDuration && categoryAllowsDuration ? (
            <DurationChip minutes={original.estimatedDuration} />
          ) : null}
        </div>
      );
    },
    sortingFn: (a, b) => {
      const aState = a.getValue<[Date, Date, ITimeOfDay]>("timespan");
      const bState = b.getValue<[Date, Date, ITimeOfDay]>("timespan");

      // Grouped rows are unlikely to have a timespan
      // If we don't have a timespan, we can't sort by it
      if (!aState || !bState) {
        return 0;
      }

      // Any time of day -> end of list, let secondary sort decide if both are Any
      if (aState[2] === "Any" && bState[2] !== "Any") {
        return 1;
      }
      if (bState[2] === "Any" && aState[2] !== "Any") {
        return -1;
      }

      if (aState[0] < bState[0]) {
        return -1;
      }
      if (aState[0] > bState[0]) {
        return 1;
      }
      if (aState[1] < bState[1]) {
        return -1;
      }
      if (aState[1] > bState[1]) {
        return 1;
      }
      return 0;
    },
  }),
  columnHelper.accessor("status", {
    header: translator(COLUMN_DISPLAY_NAMES["status"]),
    cell: ({ getValue }) => {
      const status = getValue();
      if (!status) {
        return null;
      }
      return <StatusTag status={status} variant="icon" size="small" />;
    },
    sortingFn: (a, b) => {
      const aState = a.getValue<IActivityRowStatus>("status");
      const bState = b.getValue<IActivityRowStatus>("status");

      const orderIndex = (s: IActivityRowStatus) =>
        activityRowStatusOrder.indexOf(s);

      if (orderIndex(aState) < orderIndex(bState)) {
        return -1;
      } else if (orderIndex(aState) > orderIndex(bState)) {
        return 1;
      } else {
        return 0;
      }
    },
  }),
  columnHelper.accessor("title", {
    header: translator(msg`Aktivitet`),
    sortingFn: (a, b) => {
      const aTitle = getTitleFromActivityOccurrenceOrGroup(
        a.getValue<IActivityOccurrenceOrGroup>("title"),
      );
      const bTitle = getTitleFromActivityOccurrenceOrGroup(
        b.getValue<IActivityOccurrenceOrGroup>("title"),
      );
      return aTitle.localeCompare(bTitle);
    },
    cell: ({ getValue, row }) => {
      const activityOccurrenceOrGroup = getValue();
      if (!isGroup(activityOccurrenceOrGroup)) {
        return (
          <ActivityTitle
            activityOccurrence={activityOccurrenceOrGroup}
            size="small"
            showDescriptionAndComment={expandedRowLayout}
            showRecurringIcon={expandedRowLayout}
          />
        );
      }

      const isCancelledVisit =
        activityOccurrenceOrGroup.visitStatus ===
        groupStatusSchema.enum.cancelled;
      const cancellationReason = isCancelledWithReason(
        activityOccurrenceOrGroup,
      )
        ? displayCancelGroupReason(
            activityOccurrenceOrGroup.visitCancellationReason,
            translator,
          )
        : undefined;

      return (
        <div className={styles.groupTitleCell}>
          <ul>
            {activityOccurrenceOrGroup.occurrences.map((activityOccurrence) => {
              const { id, activityId } = activityOccurrence;
              const showStatus = showStatusForActivityOccurrence({
                group: activityOccurrenceOrGroup,
              });

              const classNames = clsx(
                styles.activityTitleInGroup,
                !expandedRowLayout && styles.compactViewMode,
                row.original.activeOccurrencesInGroup.find(
                  (item) =>
                    activityId === item.activityId && id === item.occurrenceId,
                ) && styles.occurrenceIsActive,
                row.original.activeActivitiesInGroup.find(
                  (item) => activityId === item.activityId,
                ) && styles.activityIsActive,
              );

              return (
                <li key={`${activityId}-${id}`} className={classNames}>
                  <ActivityTitle
                    activityOccurrence={activityOccurrence}
                    weight="regular"
                    size="small"
                    showRecurringIcon={expandedRowLayout}
                    statusTag={
                      showStatus ? (
                        <StatusTag
                          status={activityOccurrence.status}
                          size="tiny"
                        />
                      ) : undefined
                    }
                    showDescriptionAndComment={expandedRowLayout}
                  />
                </li>
              );
            })}
          </ul>
          {isCancelledVisit ? (
            <Text
              element="span"
              weight="regular"
              size="small"
              className={styles.cancellationInfo}
            >
              <Trans>Avbokat</Trans>
              {cancellationReason ? (
                <>
                  <span className={styles.cancellationSeparator} aria-hidden />
                  {cancellationReason}
                </>
              ) : null}
            </Text>
          ) : null}
        </div>
      );
    },
  }),
  columnHelper.accessor("patient", {
    header: translator(COLUMN_DISPLAY_NAMES["patient"]),
    cell: ({ getValue }) => {
      const patient = getValue();
      if (!patient) {
        return null;
      }
      if (patient.status === patientStatusSchema.enum.deleted) {
        return getPatientNameWithStatus(patient, "full");
      }

      const hasIndicators =
        patient.flags ||
        patient.address.keyToPatient ||
        patient.pet ||
        patient.primaryResponsible;

      if (hasIndicators) {
        return (
          <div className={styles.patientWithIndicators}>
            <Text element="span" size="small">
              {getPatientNameWithStatus(patient, "full")}
            </Text>
            <div className={styles.indicators}>
              {patient.flags ? <FlagsPopover details={patient.flags} /> : null}
              {patient.address.keyToPatient ? (
                <KeyToPatientPopover details={patient.address.keyToPatient} />
              ) : null}
              {patient.pet ? <PetPopover details={patient.pet} /> : null}
              {patient.primaryResponsible ? (
                <PatientResponsiblePopover
                  employeeId={patient.primaryResponsible}
                />
              ) : null}
            </div>
          </div>
        );
      }
      return (
        <Text element="span" size="small">
          {getPatientNameWithStatus(patient, "full")}
        </Text>
      );
    },
    getGroupingValue: ({ patient }) => {
      return patient ? patient.id : undefined;
    },
    sortingFn: (a, b) => {
      const aState = a.getValue<IPatient | undefined>("patient");
      const bState = b.getValue<IPatient | undefined>("patient");

      // Sort patient-less activities to the top
      if (!aState && bState) {
        return -1;
      }
      if (aState && !bState) {
        return 1;
      }

      if (aState && bState) {
        // Sort activities with deleted patients to the bottom
        const aDeleted = aState.status === patientStatusSchema.enum.deleted;
        const bDeleted = bState.status === patientStatusSchema.enum.deleted;
        if (aDeleted && !bDeleted) {
          return 1;
        }
        if (!aDeleted && bDeleted) {
          return -1;
        }

        if (!aDeleted && !bDeleted) {
          const aName = getPatientFullName(aState.name);
          const bName = getPatientFullName(bState.name);
          if (aName < bName) {
            return -1;
          }
          if (aName > bName) {
            return 1;
          }
        }

        if (aState.id < bState.id) {
          return -1;
        }
        if (aState.id > bState.id) {
          return 1;
        }
      }

      return 0;
    },
    filterFn: (row, _columnId, filterValue) =>
      matchesPatientOrActivityTitleSearch(
        typeof filterValue === "string" ? filterValue : "",
        row.original.patient,
        row.original.title,
      ),
  }),
  columnHelper.accessor("areaDisplayName", {
    header: translator(COLUMN_DISPLAY_NAMES["areaDisplayName"]),
    cell: ({ getValue }) => {
      const areaDisplayName = getValue();
      if (!areaDisplayName) {
        return null;
      }
      return (
        <Text element="span" size="small">
          {areaDisplayName}
        </Text>
      );
    },
  }),
  ...(shouldShowTeam
    ? [
        columnHelper.accessor("team", {
          header: "",
          cell: ({ getValue }) => {
            const team = getValue();
            if (!team) {
              return null;
            }
            return <TeamBadge team={team} />;
          },
          getGroupingValue: ({ team }) => {
            if (!team) {
              return `-`;
            }
            return team.displayName;
          },
          sortingFn: (a, b) => {
            const aState = a.getValue<typeof a.original.team>("team");
            const bState = b.getValue<typeof b.original.team>("team");

            // Sort team-less activities to the top
            if (!aState && bState) {
              return -1;
            }
            if (aState && !bState) {
              return 1;
            }

            if (aState && bState) {
              return aState.displayName.localeCompare(bState.displayName);
            }

            return 0;
          },
        }),
      ]
    : []),
  columnHelper.accessor("actors", {
    header: translator(COLUMN_DISPLAY_NAMES["actors"]),
    cell: ({ getValue }) => {
      const activityOccurrenceOrGroup = getValue();
      return (
        <ActorsCell activityOccurrenceOrGroup={activityOccurrenceOrGroup} />
      );
    },
    getGroupingValue: ({ actors: activityOccurrence }) => {
      if (!isEmployeePerformed(activityOccurrence)) {
        return NOT_ASSIGNABLE;
      }
      if (activityOccurrence.assignees.length === 0) {
        return NO_ONE_ASSIGNED;
      }
      if (activityOccurrence.assignees.length > 1) {
        return MULTIPLE_ASSIGNED;
      }
      return activityOccurrence.assignees
        .map((assignee) => assignee.id)
        .join(",");
    },
    sortingFn: (a, b) => {
      // Only do assignees sorting on group level
      // On sub-row level, sort by next column, typically time
      if (a.depth > 0 && b.depth > 0) {
        return 0;
      }

      // Put unassignable activities at the bottom
      if (a.groupingValue === NOT_ASSIGNABLE) {
        return 1;
      }
      if (b.groupingValue === NOT_ASSIGNABLE) {
        return -1;
      }

      // Equivalent to checking `groupingValue` above.
      // TypeScript does not understand this relation, so we need this as typeguard.
      if (
        !isEmployeePerformed(a.original.actors) ||
        !isEmployeePerformed(b.original.actors)
      ) {
        return 0;
      }

      // Then put activities with no assignees at the bottom
      if (a.groupingValue === NO_ONE_ASSIGNED) {
        return 1;
      }
      if (b.groupingValue === NO_ONE_ASSIGNED) {
        return -1;
      }

      const getSortingValues = (
        assignees: IScheduledShift[],
        groupingValue: unknown,
      ) => {
        const assignee = assignees.find(
          (assignee) => assignee.id.toString() === groupingValue,
        );

        // Should never happen, since `groupingValue` is derived from assignees.
        // If it happens, just bail with default values.
        if (!assignee) {
          Sentry.captureException(new Error("Could not find assignee"));
          return { start: new Date(), end: new Date(), name: "" };
        }
        return {
          start: assignee.startDateTime,
          end: assignee.endDateTime,
          name: shiftName(assignee, {
            competenceFormat: "full",
            employeeNameFormat: "full",
          }),
        };
      };

      const {
        start: aStart,
        end: aEnd,
        name: aName,
      } = getSortingValues(a.original.actors.assignees, a.groupingValue);
      const {
        start: bStart,
        end: bEnd,
        name: bName,
      } = getSortingValues(b.original.actors.assignees, b.groupingValue);

      return aStart < bStart
        ? -1
        : aStart > bStart
          ? 1
          : aEnd < bEnd
            ? -1
            : aEnd > bEnd
              ? 1
              : aName < bName
                ? -1
                : aName > bName
                  ? 1
                  : 0;
    },
    filterFn: (row, columnId, filterValue) => {
      // Handle scenario where no filter is applied
      if (!filterValue) {
        return true;
      }

      const { showOnlyAssignedToMe } = filterValue;

      if (!showOnlyAssignedToMe) {
        return true;
      }

      const activityOccurrenceOrGroup =
        row.getValue<IActivityOccurrenceOrGroup>(columnId);

      const assignees = isGroup(activityOccurrenceOrGroup)
        ? activityOccurrenceOrGroup.assignees
        : activityOccurrenceOrGroup.category ===
              categorySchema.enum.PatientTask ||
            activityOccurrenceOrGroup.category ===
              categorySchema.enum.PatientMeasurementTask
          ? []
          : activityOccurrenceOrGroup.assignees;

      const currentUserId = row.original.currentUser?.id;

      if (
        assignees.some((assignee) => assignee.employee?.id === currentUserId)
      ) {
        return true;
      }

      return false;
    },
  }),
];

const GroupHeaderRow = ({
  columnCount,
  grouping,
  count,
}: {
  columnCount: number;
  grouping: IGrouping;
  count: number;
}) => {
  const { t } = useLingui();
  const groupHeaderDisplayValue = (grouping: IGrouping) => {
    if (grouping.id === "category") {
      return t(activityCategoryDictionary[grouping.value]);
    }
    if (grouping.id === "status") {
      return t(statusDictionary[grouping.value]);
    }

    // For actors we group on list of ids, but we display the list of names
    if (grouping.id === "actors") {
      if (grouping.value === NOT_ASSIGNABLE) {
        return <Trans>Inte tilldelbar</Trans>;
      }
      if (grouping.value === NO_ONE_ASSIGNED) {
        return <Trans>Ingen tilldelad</Trans>;
      }
      if (grouping.value === MULTIPLE_ASSIGNED) {
        return <Trans>Flera tilldelade</Trans>;
      }
      return grouping.value;
    }

    // We know this is just a regular string, e.g. patient id
    return grouping.value;
  };
  return (
    <tr>
      <th colSpan={columnCount} className={styles.groupHeaderCell}>
        <Heading level="h2">
          {groupHeaderDisplayValue(grouping)} ({count})
        </Heading>
      </th>
    </tr>
  );
};

const groupValue = (
  row: Row<IActivityTableItem>,
  selectedDate: Date,
  translator: ReturnType<typeof useLingui>["t"],
) => {
  // For patients we group on id, but we display the name (which in rare cases may be duplicated)
  if (row.groupingColumnId === "patient") {
    return row.original.patient
      ? row.original.patient.status === patientStatusSchema.enum.deleted
        ? translator(patientStatusDictionary.deleted.singular)
        : getPatientFullName(row.original.patient.name)
      : // If there is no patient, fallback to a hyphen
        "-";
  }

  // For actors we group on list of ids, but we display the list of names
  if (row.groupingColumnId === "actors") {
    if (row.groupingValue === NOT_ASSIGNABLE) {
      return row.groupingValue;
    }
    if (row.groupingValue === NO_ONE_ASSIGNED) {
      return row.groupingValue;
    }
    if (row.groupingValue === MULTIPLE_ASSIGNED) {
      return row.groupingValue;
    }

    // Equivalent to checking `groupingValue` above.
    // TypeScript does not understand this relation, so we need this as typeguard.
    if (!isEmployeePerformed(row.original.actors)) {
      return NOT_ASSIGNABLE;
    }

    const assignedShift = row.original.actors.assignees.find((assignee) => {
      return row.groupingValue === assignee.id.toString();
    });

    // Should never happen, since `groupingValue` is derived from assignees.
    if (!assignedShift) {
      Sentry.captureException(new Error("Could not find any assigned shifts"));
      return NO_ONE_ASSIGNED;
    }

    const {
      startedDayBefore,
      hasEndTimeAfterToday,
      formattedDayBefore,
      formattedDayToday,
    } = shiftTimeDisplayValues({
      selectedDate,
      startDateTime: assignedShift.startDateTime,
      endDateTime: assignedShift.endDateTime,
    });

    const suffix = startedDayBefore
      ? translator`Start ${formattedDayBefore}`
      : hasEndTimeAfterToday
        ? translator`Start ${formattedDayToday}`
        : "";
    const spaceForSuffix = suffix ? " " : "";

    return `${shiftName(assignedShift, { competenceFormat: "full", employeeNameFormat: "full" })} (${assignedShift.startDateTime.getHours()}-${assignedShift.endDateTime.getHours()})${spaceForSuffix}${suffix}`;
  }

  return row.groupingValue;
};

const ActivityOrVisitRow = memo(
  ({
    row,
    expandedRowLayout: _expandedRowLayout,
  }: {
    row: Row<IActivityTableItem>;
    /** Included so memo does not skip re-renders when only column defs (expanded layout) change. */
    expandedRowLayout: boolean;
  }) => {
    const { t } = useLingui();
    const navigate = useNavigate();
    const item = row.original.activityOccurrenceOrGroup;
    const isCancelledVisit =
      isGroup(item) && item.visitStatus === groupStatusSchema.enum.cancelled;

    // Temporary fix for a11y - we aim to move to React Aria Table soon
    const rowAriaLabel = isGroup(item)
      ? `${t(activityCategoryDictionary[item.category])}: ${item.occurrences.map((occurrence) => occurrence.title).join(", ")}`
      : item.title;

    const navigateToRow = () => {
      if (isGroup(item)) {
        void navigate(`occurrence-groups/${item.id}`);
      } else {
        void navigate(`${item.activityId}/occurrences/${item.id}`);
      }
    };

    return (
      <tr
        // Temporary fix for a11y - we aim to move to React Aria Table soon
        tabIndex={0}
        aria-label={rowAriaLabel}
        className={clsx(
          row.original.occurrenceIsActive && styles.occurrenceIsActive,
          row.original.activityIsActive && styles.activityIsActive,
          row.original.groupIsActive && styles.groupIsActive,
          isCancelledVisit && styles.cancelled,
        )}
        onClick={(event) => {
          // Don't navigate if the target is a button, link, or button-like element
          // E.g. FlagPopover
          const el = event.target;
          if (
            el instanceof Element &&
            el.closest('button, a, [role="button"]')
          ) {
            return;
          }
          navigateToRow();
        }}
        // Temporary fix for a11y - we aim to move to React Aria Table soon
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) {
            return;
          }
          if (event.key !== "Enter") {
            return;
          }
          navigateToRow();
        }}
      >
        {row.getVisibleCells().map((cell) => (
          <td key={cell.id}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        ))}
      </tr>
    );
  },
);
ActivityOrVisitRow.displayName = "ActivityOrVisitRow";

// When all Groups re-render, it can take several 100ms.
// Memoize, but pass expandedRowLayout so we still re-render when only the expanded column defs change (memo compares all props).
// Cleaner logic welcome (the date string manipulation is easy to break), but the plan is to remove this view soon, so not prioritized.
// Also possible to drill down further and only re-render when the "actors" grouping is selected, since that is the only one requiring the selectedDate.
const Group = memo(
  ({
    groupRow,
    selectedDate,
    translator,
    expandedRowLayout,
  }: {
    groupRow: Row<IActivityTableItem>;
    selectedDate: Date;
    translator: ReturnType<typeof useLingui>["t"];
    /** Included so memo does not skip re-renders when only column defs (expanded layout) change. */
    expandedRowLayout: boolean;
  }) => {
    const typeSafeGrouping = groupingSchema.parse({
      id: groupRow.groupingColumnId,
      value: groupValue(groupRow, selectedDate, translator),
    });

    return (
      <tbody>
        <GroupHeaderRow
          columnCount={groupRow.getVisibleCells().length}
          grouping={typeSafeGrouping}
          count={groupRow.subRows.length}
        />
        {groupRow.subRows.map((row) => (
          <ActivityOrVisitRow
            key={row.id}
            row={row}
            expandedRowLayout={expandedRowLayout}
          />
        ))}
      </tbody>
    );
  },
);
Group.displayName = "Group";

export const ActivitiesTable = () => {
  const { showOnlyAssignedToMe, setShowOnlyAssignedToMe, expandedRowLayout } =
    useActivitiesViewToolbar();
  const { t } = useLingui();
  const selectedDate = useSelectedDate();

  const {
    data: activityOccurrencesAndGroups,
    isPending,
    isError,
    error,
  } = useActivityOccurrencesAndGroups(selectedDate, selectedDate);

  const params = useParams();

  const { data: currentUser } = useCurrentEmployee();

  const { data: teams } = useTeams();
  const shouldShowTeam = Boolean(teams && teams.length > 0);

  const { data: showDuration } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.ShowDurationInUI,
  );

  const data = useMemo(
    () =>
      activityOccurrencesAndGroups?.map((activityOccurrenceOrGroup) => {
        if (isGroup(activityOccurrenceOrGroup)) {
          const group = activityOccurrenceOrGroup;
          return {
            activityOccurrenceOrGroup: group,
            category: group.category,
            timespan: [
              group.start,
              group.end,
              group.occurrences.every(
                ({ timeOfDay }) => timeOfDay === timeOfDaySchema.enum.Any,
              )
                ? timeOfDaySchema.enum.Any
                : timeOfDaySchema.enum.Specific,
            ] as [Date, Date, ITimeOfDay],
            status: calculateGroupStatus(group),
            title: group,
            actors: group,
            currentUser,
            patient: group.occurrences[0]?.patient,
            areaDisplayName: resolveAreaDisplayName(group.occurrences[0]),
            team: resolveTeam(group.occurrences[0]),
            block: group,
            groupIsActive: params.groupId === group.id,
            occurrenceIsActive: false,
            activityIsActive: false,
            activeOccurrencesInGroup: group.occurrences
              .filter(
                (occurrence) =>
                  params.activityId === occurrence.activityId &&
                  params.occurrenceId === occurrence.id,
              )
              .map((occurrence) => ({
                activityId: occurrence.activityId,
                occurrenceId: occurrence.id,
              })),
            activeActivitiesInGroup: group.occurrences
              .filter(
                (occurrence) => params.activityId === occurrence.activityId,
              )
              .map((occurrence) => ({
                activityId: occurrence.activityId,
              })),
            estimatedDuration: calculateGroupDuration(
              group.occurrences.map((o) => o.duration),
            ),
          };
        } else {
          const activityOccurrence = activityOccurrenceOrGroup;
          return {
            activityOccurrenceOrGroup: activityOccurrence,
            category: activityOccurrence.category,
            timespan: [
              activityOccurrence.start,
              activityOccurrence.end,
              activityOccurrence.timeOfDay,
            ] as [Date, Date, ITimeOfDay],
            status: activityOccurrence.status,
            title: activityOccurrence,
            patient: activityOccurrence.patient,
            areaDisplayName: resolveAreaDisplayName(activityOccurrence),
            team: resolveTeam(activityOccurrence),
            actors: activityOccurrence,
            currentUser,
            block: activityOccurrence,
            occurrenceIsActive:
              params.occurrenceId === activityOccurrenceOrGroup.id &&
              params.activityId === activityOccurrenceOrGroup.activityId,
            activityIsActive:
              params.activityId === activityOccurrenceOrGroup.activityId,
            groupIsActive: false,
            activeOccurrencesInGroup: [],
            activeActivitiesInGroup: [],
            estimatedDuration: activityOccurrence.duration,
          };
        }
      }) ?? [],
    [
      activityOccurrencesAndGroups,
      params.activityId,
      params.occurrenceId,
      params.groupId,
      currentUser,
    ],
  );

  const [grouping, setGrouping] = useState<GroupingState>(["category"]);

  const [inGroupSorting, setInGroupSorting] = useState<SortingState>([
    { id: "timespan", desc: false },
  ]);
  const sorting = useMemo(
    () => [
      ...grouping.map((group) => ({ id: group, desc: false })),
      ...inGroupSorting.map((sort) => ({
        id: sort.id,
        desc: sort.desc,
      })),
      { id: "timespan", desc: false },
      { id: "title", desc: false },
    ],
    [grouping, inGroupSorting],
  );

  const [patientSearchValue, setPatientSearchValue] = useState<string>("");

  const columnFilters = useMemo(() => {
    return [
      {
        id: "patient",
        value: patientSearchValue,
      },
      {
        id: "actors",
        value: { showOnlyAssignedToMe },
      },
    ];
  }, [patientSearchValue, showOnlyAssignedToMe]);

  const translatedColumns = columns(
    t,
    shouldShowTeam,
    showDuration ?? false,
    expandedRowLayout,
  );
  // oxlint-disable-next-line react/incompatible-library -- TanStack Table's instance cannot be memoized by React Compiler.
  const table = useReactTable({
    data,
    columns: translatedColumns,
    getCoreRowModel: getCoreRowModel(),
    getGroupedRowModel: customGetGroupedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    groupedColumnMode: false, // Don't move grouped columns to the left
    state: {
      grouping,
      sorting,
      columnFilters,
    },
  });

  if (isError) {
    Sentry.captureException(error);
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta in aktiviteterna.`} ${deducedError(error)}`}
        padding={24}
      />
    );
  }

  const chosenDate = dateName(selectedDate);

  const patientFilterIsActive =
    table.getColumn("patient")?.getFilterValue() !== "";
  const actorsFilterIsActive = (
    table.getColumn("actors")?.getFilterValue() as {
      showOnlyAssignedToMe: boolean;
    }
  )?.showOnlyAssignedToMe;
  const filtersAreActive = patientFilterIsActive || actorsFilterIsActive;

  return (
    <>
      <div className={styles.absoluteContainer}>
        <SplitPane panelSizeLocalStorageKey="activities">
          {isPending ? (
            <Loading message={t`Hämtar aktiviteter`} padding={24} />
          ) : data.length === 0 ? (
            <NoResults
              message={t`Det finns inga aktiviteter ${chosenDate}`}
              padding={24}
            />
          ) : (
            <div className={styles.tableContainer}>
              <article className={styles.tableToolbar}>
                <ControlledSelect
                  size="compact"
                  label={t`Gruppering`}
                  value={grouping[0] ?? NO_GROUPING_VALUE}
                  onChange={(value) =>
                    setGrouping(value === NO_GROUPING_VALUE ? [] : [value])
                  }
                >
                  <option value={NO_GROUPING_VALUE}>
                    {t`Ingen gruppering`}
                  </option>
                  {GROUPABLE_COLUMNS.map((column) => {
                    if (column === "team" && !shouldShowTeam) {
                      return null;
                    }
                    return (
                      <option key={column} value={column}>
                        {t(COLUMN_DISPLAY_NAMES[column])}
                      </option>
                    );
                  })}
                </ControlledSelect>
                <ControlledSelect
                  size="compact"
                  label={t`Ordning`}
                  value={inGroupSorting[0]?.id ?? t`Välj ordning`}
                  onChange={(value) =>
                    setInGroupSorting([{ id: value, desc: false }])
                  }
                >
                  <option value={t`Välj ordning`} disabled>
                    {t`Välj ordning`}
                  </option>
                  {SORTABLE_COLUMNS.map((column) => (
                    <option key={column} value={column}>
                      {t(COLUMN_DISPLAY_NAMES[column])}
                    </option>
                  ))}
                </ControlledSelect>
                <search>
                  <ControlledInput
                    type="search"
                    label={t`Sök patient/aktivitet`}
                    size="compact"
                    value={patientSearchValue}
                    onChange={(e) => setPatientSearchValue(e.target.value)}
                  />
                </search>
                <Fieldset legend={t`Övriga filter`}>
                  <div className={styles.filters}>
                    <StandaloneCheckbox
                      label={t`Mina aktiviteter`}
                      name="only-assigned-to-me"
                      checked={showOnlyAssignedToMe}
                      onChange={(e) =>
                        setShowOnlyAssignedToMe(e.target.checked)
                      }
                    />
                  </div>
                </Fieldset>
              </article>

              {filtersAreActive ? (
                <>
                  <div className={styles.infoWrapper}>
                    <InlineAlert
                      type="info"
                      title={t`Listan är filtrerad`}
                      placement="fill"
                      actions={[
                        {
                          label: t`Rensa filter`,
                          onClick: () => {
                            setPatientSearchValue("");
                            setShowOnlyAssignedToMe(false);
                          },
                        },
                      ]}
                    />
                  </div>
                  {table.getRowModel().rows.length === 0 ? (
                    <NoResults
                      message={t`Inga aktiviteter med nuvarande filter hittades`}
                    />
                  ) : null}
                </>
              ) : null}
              <table
                className={styles.activitiesTable}
                key={selectedDate.toDateString()}
              >
                <thead className={styles.thead}>
                  {table.getHeaderGroups().map((headerGroup) => {
                    return (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <th
                            className={styles.tableHeaderCell}
                            key={header.id}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                          </th>
                        ))}
                      </tr>
                    );
                  })}
                </thead>
                {/* Grouping logic/markup inspired by https://github.com/TanStack/table/discussions/3320#discussioncomment-1453996 */}
                {grouping.length === 0 ? (
                  <tbody>
                    {table.getRowModel().rows.map((row) => (
                      <ActivityOrVisitRow
                        key={row.id}
                        row={row}
                        expandedRowLayout={expandedRowLayout}
                      />
                    ))}
                  </tbody>
                ) : (
                  table
                    .getRowModel()
                    .rows.map((row) => (
                      <Group
                        key={row.id}
                        groupRow={row}
                        selectedDate={selectedDate}
                        translator={t}
                        expandedRowLayout={expandedRowLayout}
                      />
                    ))
                )}
              </table>
            </div>
          )}
          <Outlet />
        </SplitPane>
      </div>
    </>
  );
};
